/**
 * ErrorBoundary — render-crash containment for the Studio shell.
 *
 * Why: React unmounts the ENTIRE tree when any component throws during render
 * and nothing catches it. On 2026-07-27 one orphaned identifier in a journey
 * preview pane (`brand` — see changelog/2026-07-27T1730) blanked the whole app
 * to grey: no rail, no topbar, no way back. A boundary turns that class of
 * failure into a contained, honest panel with a way out.
 *
 * Three variants, deliberately different in weight:
 *   app    — last resort around the whole shell. Full-page; offers a reload.
 *   screen — one per route (AppRouter). The rail/topbar survive, so the user
 *            can navigate away; auto-clears when the route changes.
 *   pane   — an embedded region (live preview, editor canvas, chart). Inline,
 *            sized to its container; the rest of the screen keeps working.
 *
 * This reports; it does not repair. The panel names the failing surface and
 * shows the real error text — never a cheerful euphemism that hides a defect.
 */
import React from "react";
import { useT } from "../lib/i18n";

type Variant = "app" | "screen" | "pane";

interface Props {
  children: React.ReactNode;
  /** Stable id of the guarded surface — route id, or "<screen>:<pane>". */
  scope: string;
  /** Human label for the panel ("Journey preview"). Defaults to `scope`. */
  label?: string;
  variant?: Variant;
  /** Changing this value clears a caught error (AppRouter passes the route). */
  resetKey?: string;
  /**
   * Renders the fallback inside a chrome wrapper. Screens own their own rail +
   * topbar (they render `<Screen>` themselves), so without this a screen crash
   * would take the navigation with it and strand the user — AppRouter passes a
   * bare `<Screen>` so the rail stays usable.
   */
  wrap?: (fallback: React.ReactNode) => React.ReactNode;
}
interface State { error: Error | null; stack: string; retried: boolean; failedAfterRetry: boolean }

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, stack: "", retried: false, failedAfterRetry: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // `retried` is only true when the user pressed Try again, so this flags a
    // genuine repeat — not React's double-invoke in development.
    this.setState((s) => ({ stack: info.componentStack || "", failedAfterRetry: s.retried }));
    // Keep the real error in the console for anyone debugging the preview.
    console.error(`[render crash] ${this.props.scope}:`, error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) {
      this.setState({ error: null, stack: "", retried: false, failedAfterRetry: false });
    }
  }

  retry = () => this.setState({ error: null, stack: "", retried: true });

  render() {
    if (!this.state.error) return this.props.children;
    const panel = (
      <CrashPanel
        variant={this.props.variant ?? "screen"}
        label={this.props.label ?? this.props.scope}
        scope={this.props.scope}
        error={this.state.error}
        stack={this.state.stack}
        repeated={this.state.failedAfterRetry}
        onRetry={this.retry}
      />
    );
    return this.props.wrap ? this.props.wrap(panel) : panel;
  }
}

/** Convenience wrapper for an embedded region. */
export function PaneBoundary({ scope, label, resetKey, children }: Omit<Props, "variant">) {
  return <ErrorBoundary variant="pane" scope={scope} label={label} resetKey={resetKey}>{children}</ErrorBoundary>;
}

function CrashPanel({ variant, label, scope, error, stack, repeated, onRetry }: {
  variant: Variant; label: string; scope: string; error: Error; stack: string; repeated: boolean; onRetry: () => void;
}) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const message = (error && error.message) || String(error);
  const details = `${scope}\n${error?.name || "Error"}: ${message}\n${error?.stack || ""}\n--- component stack ---${stack}`;
  const copy = () => {
    try {
      void navigator.clipboard.writeText(details);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable in some preview hosts */ }
  };

  const pane = variant === "pane";
  const wrap: React.CSSProperties = variant === "app"
    ? { minHeight: "100vh", display: "grid", placeItems: "center", padding: 40, background: "var(--jrni-color-surface-canvas)" }
    : pane
      ? { display: "flex", alignItems: "center", justifyContent: "center", padding: 20, minHeight: 180, height: "100%" }
      : { minHeight: "60vh", display: "grid", placeItems: "center", padding: 40 };

  return (
    <div style={{ ...wrap, fontFamily: "var(--jrni-font-family-sans)" }} role="alert" data-crash-scope={scope}>
      <div style={{
        maxWidth: pane ? 380 : 560, width: "100%", background: "var(--jrni-color-surface-raised, #fff)",
        border: "1px solid var(--jrni-color-neutral-5)", borderRadius: 12, padding: pane ? "18px 20px" : "26px 28px",
        boxShadow: pane ? "none" : "0 10px 30px -22px rgba(20,16,46,0.5)",
      }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span aria-hidden="true" style={{
            width: 22, height: 22, borderRadius: 999, flexShrink: 0, display: "grid", placeItems: "center",
            background: "var(--jrni-color-semantic-red-4, #FDECEF)", color: "var(--jrni-color-semantic-red-1, #C4123F)",
            fontSize: 13, fontWeight: 800, marginTop: 1,
          }}>!</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: pane ? 14 : 16.5, fontWeight: 700, color: "var(--jrni-color-neutral-1)", marginBottom: 4 }}>
              {variant === "app"
                ? t("shell.errorBoundary.titleApp", "Studio hit an error")
                : pane
                  ? t("shell.errorBoundary.titlePane", "This panel didn't load")
                  : t("shell.errorBoundary.titleScreen", "This screen didn't load")}
            </div>
            <div style={{ fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11, color: "var(--jrni-color-text-soft, #6B7280)", marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: 12.5, color: "var(--jrni-color-neutral-2)", lineHeight: 1.55, textWrap: "pretty" }}>
              {variant === "app"
                ? t("shell.errorBoundary.bodyApp", "The app hit an error it couldn't recover from. Reloading usually clears it — if it happens again, send the details below.")
                : pane
                  ? t("shell.errorBoundary.bodyPane", "This panel failed to render. The rest of the screen still works.")
                  : t("shell.errorBoundary.bodyScreen", "This screen failed to render. Everything else still works — you can switch screens from the menu.")}
            </div>
            <div style={{
              marginTop: 10, padding: "8px 10px", borderRadius: 8, background: "var(--jrni-color-neutral-6, #F4F5F7)",
              fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11.5,
              color: "var(--jrni-color-neutral-2)", wordBreak: "break-word", lineHeight: 1.45,
            }}>{message}</div>
            {repeated ? (
              <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--jrni-color-semantic-orange-1, #9A5B00)" }}>
                {t("shell.errorBoundary.repeated", "It failed again after retrying — this needs a code fix, not another attempt.")}
              </div>
            ) : null}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
              {variant === "app" ? (
                <button onClick={() => window.location.reload()} style={btn(true)}>
                  {t("shell.errorBoundary.reload", "Reload the app")}
                </button>
              ) : (
                <button onClick={onRetry} style={btn(true)}>
                  {t("shell.errorBoundary.retry", "Try again")}
                </button>
              )}
              <button onClick={copy} style={btn(false)}>
                {copied ? t("shell.errorBoundary.copied", "Copied") : t("shell.errorBoundary.copyDetails", "Copy error details")}
              </button>
              <button onClick={() => setOpen((o) => !o)} style={btn(false)}>
                {open ? t("shell.errorBoundary.hideTechnical", "Hide technical detail") : t("shell.errorBoundary.showTechnical", "Technical detail")}
              </button>
            </div>
            {open ? (
              <pre style={{
                marginTop: 12, marginBottom: 0, maxHeight: 220, overflow: "auto", padding: 10, borderRadius: 8,
                background: "var(--jrni-color-neutral-6, #F4F5F7)", border: "1px solid var(--jrni-color-neutral-5)",
                fontFamily: "var(--jrni-font-family-mono, ui-monospace, monospace)", fontSize: 11,
                color: "var(--jrni-color-neutral-2)", whiteSpace: "pre-wrap", lineHeight: 1.5,
              }}>{details}</pre>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function btn(primary: boolean): React.CSSProperties {
  return {
    height: 30, padding: "0 12px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
    fontSize: 12.5, fontWeight: 600,
    border: primary ? "1px solid var(--jrni-color-primary-1)" : "1px solid var(--jrni-color-neutral-5)",
    background: primary ? "var(--jrni-color-primary-1)" : "transparent",
    color: primary ? "#fff" : "var(--jrni-color-neutral-2)",
  };
}
